import { useContext, useEffect, useRef, useState } from 'react';

import { Text } from '@gympass/yoga';
import { Close } from '@gympass/yoga-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDateHour } from 'utils/helpers';

import { NotificationContext } from 'context';
import { SpecialistType } from 'services/index';
import { getPatientRecordsEntries } from 'services/user';
import { createPatientRecordsEntry } from 'services/user/user';

import {
  Drawer,
  RecordEntriesErrorState,
  RecordEntriesLoadingSkeleton,
  RecordEntriesNotFound,
} from '..';

import ConfirmationStep from './components/ConfirmationStep';

import { DrawerPatientRecordProps } from './types';

import * as S from './styles';

const DrawerPatientRecord: React.FC<DrawerPatientRecordProps> = ({
  openDrawer,
  onCloseDrawer,
  userCognitoId,
  patientRecordId,
  questionId,
  titleKey,
  labelKey,
  tooltipDescription,
  handleCreationSuccess,
  handleCreationError,
}) => {
  const intl = useIntl();
  const { setNotification } = useContext(NotificationContext);
  const formRef = useRef<HTMLDivElement>(null);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [showConfirmationStep, setShowConfirmationStep] = useState(false);
  const [formHeight, setFormHeight] = useState(0);

  const { data, isError, isLoading } = useQuery({
    enabled: !!(userCognitoId && patientRecordId && questionId),
    queryKey: [
      userCognitoId,
      patientRecordId,
      questionId,
      'patientRecordEntries',
    ],
    queryFn: () =>
      getPatientRecordsEntries(userCognitoId, patientRecordId, questionId),
  });

  const {
    mutate: createPatientRecordEntryMutation,
    isLoading: isLoadingCreatePatientRecordEntry,
  } = useMutation({
    mutationFn: createPatientRecordsEntry,
    onSuccess: () => {
      setShowConfirmationStep(false);
      setTextAreaValue('');
      setNotification({
        message: intl.formatMessage({
          id: 'PSYCHOLOGICALRECORD.SNACKBAR.SUCCESSFEEDBACK',
        }),
        type: 'success',
      });
      handleCreationSuccess();
    },
    onError: () => {
      setShowConfirmationStep(false);
      setTextAreaValue('');
      setNotification({
        message: intl.formatMessage({
          id: 'PSYCHOLOGICALRECORD.SNACKBAR.ERRORFEEDBACK',
        }),
        type: 'failure',
      });
      handleCreationError();
    },
  });

  const specialistTypeMap = {
    [SpecialistType.CP]: intl.formatMessage({
      id: 'PSYCHOLOGICALRECORD.VARIABLE.ROLECP',
    }),
    [SpecialistType.TC]: intl.formatMessage({
      id: 'PSYCHOLOGICALRECORD.VARIABLE.ROLETC',
    }),
  };

  useEffect(() => {
    if (formRef.current) {
      setFormHeight(formRef.current.offsetHeight);
    }
  }, [formRef.current]);

  return (
    <Drawer
      onCloseDrawer={() => {
        setTextAreaValue('');
        onCloseDrawer();
      }}
      openDrawer={openDrawer}
    >
      <S.Container>
        {!showConfirmationStep ? (
          <>
            <S.NewPatientRecordEntryContainer ref={formRef}>
              <S.HeaderNewEntry>
                <S.TitleContainer>
                  <S.DrawerTitle data-testid="drawerTitle">
                    <FormattedMessage id="PSYCHOLOGICALRECORD.SECTIONTITLE.ADDINFO" />
                  </S.DrawerTitle>
                </S.TitleContainer>
                <S.CloseIcon
                  onClick={() => {
                    setTextAreaValue('');
                    onCloseDrawer();
                  }}
                  icon={Close}
                  inverted
                />
              </S.HeaderNewEntry>
              <Text.H3 data-testid="formTitle">
                <FormattedMessage id={titleKey} />
              </Text.H3>
              <S.NewPatientRecordEntrySubtitle>
                {tooltipDescription && (
                  <FormattedMessage id={tooltipDescription} />
                )}
              </S.NewPatientRecordEntrySubtitle>
              <S.NewPatientRecordEntryTextArea
                full
                maxLength={500}
                multiline
                helper={intl.formatMessage({
                  id: 'PSYCHOLOGICALRECORD.FIELDINFO.CHARACTERLIMIT',
                })}
                label={intl.formatMessage({ id: labelKey })}
                value={textAreaValue}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setTextAreaValue(e.target.value)
                }
                data-testid="newEntryInput"
              />
              <S.NewPatientRecordEntrySaveButton
                onClick={() =>
                  textAreaValue.length > 0 && setShowConfirmationStep(true)
                }
                data-testid="createNewEntryButton"
              >
                <FormattedMessage id="PSYCHOLOGICALRECORD.BUTTON.SAVE" />
              </S.NewPatientRecordEntrySaveButton>
            </S.NewPatientRecordEntryContainer>

            <S.PatientRecordEntryHistory formHeight={formHeight}>
              <S.HistoryTitle data-testid="historyTitle">
                <FormattedMessage id="PSYCHOLOGICALRECORD.TITLE.HISTORY" />
              </S.HistoryTitle>
              {!isLoading &&
                !isError &&
                data &&
                data.results?.length > 0 &&
                data?.results.map(entry => (
                  <S.RecordEntry key={entry.id} data-testid="recordEntry">
                    <S.RecordEntryInfo data-testid="recordEntryInfo">
                      {formatDateHour(entry.date, {
                        separator: ', ',
                        locale: intl.locale,
                      })}{' '}
                      | {entry.specialist_name} –{' '}
                      {
                        specialistTypeMap[
                          entry.specialist_type as SpecialistType
                        ]
                      }
                    </S.RecordEntryInfo>
                    <S.RecordEntryContent data-testid="recordEntryContent">
                      {entry.content}
                    </S.RecordEntryContent>
                  </S.RecordEntry>
                ))}
              {!isLoading &&
                !isError &&
                data &&
                !(data.results?.length > 0) && <RecordEntriesNotFound />}
              {isError && <RecordEntriesErrorState />}
              {isLoading && <RecordEntriesLoadingSkeleton />}
            </S.PatientRecordEntryHistory>
          </>
        ) : (
          <ConfirmationStep
            titleKey={titleKey}
            isLoading={isLoadingCreatePatientRecordEntry}
            onConfirm={() =>
              createPatientRecordEntryMutation({
                userCognitoId,
                patientRecordId,
                questionId,
                content: textAreaValue,
              })
            }
            onBack={() => setShowConfirmationStep(false)}
          />
        )}
      </S.Container>
    </Drawer>
  );
};

export default DrawerPatientRecord;
